import axios from 'axios';

const client = axios.create({
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Busy（ローディング）状態の簡易管理。
 *
 * 目的:
 * - 「Summary / LogList の取得中だけ」画面全体にローディング表示を出したい。
 * - そのために、対象のリクエストだけをカウントできる仕組みを用意する。
 *
 * 使い方:
 *   client.get(url, { meta: { busyKey: 'mainFetch' } })
 *
 * - meta.busyKey を付与したリクエストだけがカウント対象になります。
 * - busyKey ごとに同時リクエスト数を数えます（例: { mainFetch: 2 }）。
 */
const busyCountByKey = Object.create(null);
const busyListeners = new Set();

/** 現在のカウントを「新しいオブジェクト」として返す（参照共有を避ける） */
const getBusyCountsSnapshot = () => ({ ...busyCountByKey });

/** 購読者へ通知（購読者側の例外で全体が止まらないように保護） */
const notifyBusyListeners = () => {
  const snapshot = getBusyCountsSnapshot();
  busyListeners.forEach(fn => {
    try {
      fn(snapshot);
    } catch (_e) {
      // 購読者側の例外は無視（通知ループを止めない）
    }
  });
};

const incrementBusy = key => {
  if (!key) return;
  busyCountByKey[key] = (busyCountByKey[key] || 0) + 1;
  notifyBusyListeners();
};

const decrementBusy = key => {
  if (!key) return;
  if (!busyCountByKey[key]) return;

  busyCountByKey[key] -= 1;
  if (busyCountByKey[key] <= 0) delete busyCountByKey[key];
  notifyBusyListeners();
};

/**
 * busy カウントの購読。
 * - 返り値は unsubscribe 関数。
 * - 購読登録直後に、現在状態を1回通知します。
 */
export const subscribeBusy = fn => {
  busyListeners.add(fn);

  // 現在値をすぐ返して、初期表示のタイミングを揃える
  try {
    fn(getBusyCountsSnapshot());
  } catch (_e) {
    // 購読者側の例外は無視
  }

  return () => busyListeners.delete(fn);
};

// 起動時に localStorage を読んで、認証ヘッダを初期化する（管理画面ログイン用）
// ※AuthContext 側でも同様の初期化を行うが、万が一 AuthContext が経由されないケースでも
//   リクエストが通るように、ここでも保険として設定している。
const token = localStorage.getItem('parent_basic_auth');
if (token) {
  client.defaults.headers.common['Authorization'] = `Basic ${token}`;
}

/**
 * Axios の interceptor（リクエストの前後処理）:
 * - meta.busyKey が指定されたリクエストのみカウントする。
 * - 成功（response）/失敗（error）のどちらでも確実にカウントを減らす。
 */
client.interceptors.request.use(
  config => {
    const key = config?.meta?.busyKey;
    if (key) incrementBusy(key);
    return config;
  },
  error => {
    const key = error?.config?.meta?.busyKey;
    if (key) decrementBusy(key);
    return Promise.reject(error);
  }
);

client.interceptors.response.use(
  response => {
    const key = response?.config?.meta?.busyKey;
    if (key) decrementBusy(key);
    return response;
  },
  error => {
    const key = error?.config?.meta?.busyKey;
    if (key) decrementBusy(key);
    return Promise.reject(error);
  }
);

export default client;
